/**
 * Soft-deleted follow-ups (PRD 10.7).
 *
 * Admin-only landing page that lists trashed follow-ups and lets the
 * Admin restore them. Restoring a follow-up always succeeds (unlike a
 * deal, follow-ups don't have a "one open per parent" partial-unique
 * constraint -- you can have arbitrarily many on a deal).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";

import { BackLink } from "../components/BackLink";

import { ApiError, type TrashedFollowUp, followupsApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";

export const Route = createFileRoute("/_app/followups/trash")({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (user && user.role !== "admin") {
      throw redirect({ to: "/followups" });
    }
  },
  component: TrashPage,
});

function TrashPage() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["followups-trash", { limit: 50, offset: 0 }],
    queryFn: () => followupsApi.listTrash({ limit: 50, offset: 0 }),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <header className="space-y-1">
        <BackLink fallbackTo="/followups" fallbackLabel="follow-ups" />
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Trash2 className="size-5 opacity-70" aria-hidden />
          Trash
        </h1>
        <p className="text-sm opacity-60">
          Soft-deleted follow-ups. Restore brings them back; the parent deal must still exist
          (otherwise the row was hard-deleted with its parent).
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm opacity-60">Loading...</p>
      ) : error ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          Couldn't load trash: {(error as Error).message}
        </div>
      ) : !data || data.items.length === 0 ? (
        <Empty />
      ) : (
        <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-xl border border-[var(--border)]">
          {data.items.map((f) => (
            <TrashRow key={f.id} followup={f} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Empty() {
  return (
    <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
      <Trash2 className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
      <p className="text-sm font-medium">Trash is empty</p>
      <p className="mt-1 text-sm opacity-60">Deleted follow-ups will appear here.</p>
    </div>
  );
}

function parentLabel(followup: TrashedFollowUp): string {
  if (followup.parent_type === "none" || followup.parent_id === null) {
    return "Standalone";
  }
  const kind =
    followup.parent_type === "contact"
      ? "Contact"
      : followup.parent_type === "account"
        ? "Account"
        : "Deal";
  return `${kind} ${followup.parent_id.slice(0, 8)}…`;
}

function TrashRow({ followup }: { followup: TrashedFollowUp }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const restore = useMutation({
    mutationFn: () => followupsApi.restore(followup.id),
    onSuccess: (restored) => {
      queryClient.setQueryData(["followup", followup.id], restored);
      queryClient.invalidateQueries({ queryKey: ["followups"] });
      queryClient.invalidateQueries({ queryKey: ["followups-trash"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.reauthRequired) {
        setError("For security, please log out and log back in to confirm this restore.");
        return;
      }
      setError(err instanceof ApiError ? err.message : "Could not restore.");
    },
  });

  const due = new Date(followup.scheduled_at);

  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 space-y-0.5">
        <div className="text-sm font-medium">Follow-up due {due.toLocaleString()}</div>
        {followup.note && <p className="text-sm opacity-80">{followup.note}</p>}
        <div className="text-xs opacity-60">
          {parentLabel(followup)} &middot; Deleted {new Date(followup.deleted_at).toLocaleString()}
        </div>
        {error && <div className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</div>}
      </div>
      <button
        type="button"
        disabled={restore.isPending}
        onClick={() => {
          setError(null);
          restore.mutate();
        }}
        className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm font-medium hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/5"
      >
        <RotateCcw className="size-4" aria-hidden />
        {restore.isPending ? "Restoring..." : "Restore"}
      </button>
    </li>
  );
}
